import type { Timestamp } from 'firebase-admin/firestore';
import type { CartItem } from '@pedix-workspace/utils';

export const WHATSAPP_BOT_CONFIGURATIONS_COLLECTION = 'whatsappBotConfigurations';
export const WHATSAPP_BOT_MESSAGE_EVENTS_COLLECTION = 'whatsappBotMessageEvents';
export const WHATSAPP_BOT_ATTENTION_ITEMS_COLLECTION = 'whatsappBotAttentionItems';
export const WHATSAPP_BOT_RATE_LIMIT_COUNTERS_COLLECTION = 'whatsappBotRateLimitCounters';
export const WHATSAPP_BOT_IDEMPOTENCY_KEYS_COLLECTION = 'whatsappBotIdempotencyKeys';
export const WHATSAPP_BOT_CHATS_COLLECTION = 'whatsappBotChats';
export const WHATSAPP_BOT_CART_DRAFTS_COLLECTION = 'whatsappBotCartDrafts';

export const WHATSAPP_BOT_CUSTOM_INSTRUCTIONS_MAX_LENGTH = 4000;

export const ORDER_NOTIFICATION_TOGGLES = ['ready', 'onTheWay', 'delivered', 'courier'] as const;

export type OrderNotificationToggle = (typeof ORDER_NOTIFICATION_TOGGLES)[number];

export function normalizeDisabledOrderNotifications(raw: unknown): OrderNotificationToggle[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return ORDER_NOTIFICATION_TOGGLES.filter(toggle => raw.includes(toggle));
}

export const WHATSAPP_BOT_CREDIT_SOURCE = 'whatsapp-bot';
export const WHATSAPP_CAMPAIGN_CREDIT_SOURCE = 'whatsapp-campaign';

export type WhatsappBotMessageDirection = 'inbound' | 'outbound';

export const WHATSAPP_BOT_MESSAGE_ORIGINS = [
  'cloud_api',
  'business_app',
  'history_sync',
  'other_app',
  'meta_business_agent',
] as const;

export type WhatsappBotMessageOrigin = (typeof WHATSAPP_BOT_MESSAGE_ORIGINS)[number];

export interface WhatsappBotMessageEvent {
  establishmentId: string;
  conversationId: string | null;
  created: Date | Timestamp;
  ttl?: Date | Timestamp;
  details: {
    phoneNumberId: string;
    messageId: string;
    direction: WhatsappBotMessageDirection;
    origin?: WhatsappBotMessageOrigin | null;
    messageType: string | null;
    billable: boolean | null;
    category: string | null;
    eventType: string;
    ts: string | null;
  };
}

export type WhatsappBotAttentionReason =
  | 'handoff'
  | 'no_credit'
  | 'bot_disabled'
  | 'rate_limited'
  | 'execution_failed'
  | 'unanswered';

/**
 * Un ítem de atención = una conversación que necesita atención humana (handoff del bot o sin saldo).
 * Doc id = conversationId (determinístico → idempotente: un mismo handoff no duplica). Se borra al
 * resolverse (fin de conversación, recarga o "volver a IA"). Lo escribe el dispatcher de
 * integrations vía getPedixappDb(); lo lee el admin en vivo para el widget del header.
 */
export interface WhatsappBotAttentionItem {
  establishmentId: string;
  reason: WhatsappBotAttentionReason;
  created: Date | Timestamp;
  details: {
    conversationId: string;
    phoneNumberId: string | null;
    contactPhone: string | null;
    contactName: string | null;
    repliedAt?: Date | Timestamp | null;
    repliedBy?: string | null;
    pausedFrom?: 'business_app' | null;
  };
}

export interface WhatsappBotRateLimitWindowCount {
  bucket: number;
  count: number;
}

export interface WhatsappBotRateLimitCounter {
  establishmentId: string;
  ttl: Date | Timestamp;
  details: {
    phone: string;
    minute: WhatsappBotRateLimitWindowCount;
    hour: WhatsappBotRateLimitWindowCount;
  };
}

export interface WhatsappBotConfiguration {
  kapsoCustomerId: string | null;
  phoneNumberId: string | null;
  botEnabled: boolean;
  /**
   * Instrucciones extra por comercio que se inyectan al system prompt del agente (tono, reglas
   * propias, aclaraciones del negocio). Opcional; si está vacío el agente usa solo el prompt base.
   */
  customInstructions?: string | null;
  disabledOrderNotifications?: OrderNotificationToggle[];
  details: {
    wabaId: string | null;
    displayPhoneNumber: string | null;
    connectedAt: Date | Timestamp | null;
    triggerId: string | null;
    activationStartedAt?: Date | Timestamp | null;
  };
}

export interface WhatsappBotChatMessage {
  role: 'user' | 'assistant';
  content: unknown;
}

export interface WhatsappBotTeamAnswer {
  questions: string[];
  answer: string;
  at: number;
}

export interface WhatsappBotChat {
  establishmentId: string;
  updated: Date | Timestamp;
  ttl: Date | Timestamp;
  details: {
    phoneKey: string;
    messages: WhatsappBotChatMessage[];
    linksSent: string[];
    lastInboundAt: number | null;
    lastInboundMessageId: string | null;
    processedInboundIds?: string[];
    lastConversationId: string | null;
    lastTurnAt: number | null;
    turnLeaseUntil?: number | null;
    teamAnswers?: WhatsappBotTeamAnswer[];
  };
}

export interface WhatsappBotCartDraft {
  establishmentId: string;
  created: Date | Timestamp;
  ttl: Date | Timestamp;
  details: {
    cartItems: CartItem[];
    subtotal: number;
  };
}
